import React, { useContext, useEffect, useRef, useState } from 'react';
import { getConfig } from 'utils/helperMethods';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { commercialForecastGraphParser } from 'parser/commercialForecastParser';
const errorMessage: string = 'Something went wrong!';

// api with parser logic
interface Props {
    url: string;
    method: 'post' | 'get' | 'put' | 'delete';
    queryKey: any[];
    onComplete?: Function;
    onError?: Function;
    variables?: any;
    appConfig?: any;
    queryConfig?: {
        enabled?: boolean;
    };
}
interface outputType {
    data: any;
    loading: boolean;
    error: any;
    count: number;
}

function useFetchCommercialForecastGraphData({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    appConfig = {},
    queryConfig = {},
}: Props) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState(0);
    const mergedData = useRef<any[] | null>(null); //using ref here to avoid unnecessary render as this is just used to store data and not display in UI
    const commonConfig = getConfig('commonConfig', appConfig);
    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setData(null);
                setError(null);
            }
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const { graph_data: commercialForecastGraphData, total_count } = payload;
            // const growthAnalysisAttributeLevelData = payload;
            let mergedDataRes: any = [];
            //if merged data available and offset is different from default offset value, it means request coming from infinite scroll
            if (
                mergedData.current &&
                Array.isArray(mergedData.current) &&
                mergedData?.current.length > 0 &&
                variables?.offset !== 0
            ) {
                const temp = [...mergedData.current];
                //merge total sales, old data, new data
                mergedDataRes = [...temp, ...(commercialForecastGraphData?.slice(1) || [])]; //remove total
            } else mergedDataRes = [...(commercialForecastGraphData || [])];
            mergedData.current = mergedDataRes;
            if (mergedDataRes?.length === 0) {
                throw 'No Data';
            }
            const parsedChartData = commercialForecastGraphParser(mergedDataRes, variables, appConfig);
            setData({ chartData: parsedChartData });
            setCount(total_count ?? 0);
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        count,
    };
    return outputData;
}

export { useFetchCommercialForecastGraphData };
